import React from 'react';
import Header from '@/components/Header';
import Head from 'next/head';
import { useEffect, useState } from 'react';
import ImageOptimizer from '../components/ImageOptimizer';

export default function HomePage() {
  const [expandedFaqs, setExpandedFaqs] = React.useState({1: true});
  const [searchTerm, setSearchTerm] = React.useState("");
  const [formSubmitting, setFormSubmitting] = React.useState(false);
  const [formSubmitted, setFormSubmitted] = React.useState(false);
  const [showModal, setShowModal] = React.useState(false);
  const [showWhatsappPopup, setShowWhatsappPopup] = React.useState(false);
  
  // Mostrar popup do WhatsApp após 5 segundos quando o componente montar
  React.useEffect(() => {
    // Usar uma variável para verificar se está no cliente
    if (typeof window !== 'undefined') {
      const timer = setTimeout(() => {
        setShowWhatsappPopup(true);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, []);
  
  const toggleFaq = (id) => {
    setExpandedFaqs(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  
  // Função para aplicar máscara de telefone
  const applyPhoneMask = (value) => {
    if (!value) return '';
    
    // Remove todos os caracteres não numéricos
    value = value.replace(/\D/g, '');
    
    // Aplica a máscara (XX) XXXXX-XXXX
    if (value.length <= 2) {
      return value;
    } else if (value.length <= 7) {
      return `(${value.slice(0, 2)}) ${value.slice(2)}`;
    } else {
      return `(${value.slice(0, 2)}) ${value.slice(2, 7)}-${value.slice(7, 11)}`;
    }
  };
  
  // Função para lidar com a entrada de telefone
  const handlePhoneInput = (e) => {
    const input = e.target;
    const maskedValue = applyPhoneMask(input.value);
    input.value = maskedValue;
  };
  
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    
    const formData = new FormData(e.target);
    const formValues = Object.fromEntries(formData.entries());
    
    try {
      const response = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nome: formValues.nome,
          email: formValues.email,
          telefone: formValues.telefone,
          empresa: formValues.empresa,
          interesse: formValues.interesse,
          mensagem: formValues.mensagem,
          origem: 'site',
        })
      });
      
      if (response.ok) {
        setFormSubmitted(true);
        e.target.reset();
        setShowModal(true);
        setTimeout(() => setFormSubmitted(false), 5000);
      } else {
        alert("Ocorreu um erro ao enviar o formulário. Por favor, tente novamente.");
      }
    } catch (error) {
      console.error("Erro ao enviar formulário:", error);
      alert("Ocorreu um erro ao enviar o formulário. Por favor, tente novamente.");
    } finally {
      setFormSubmitting(false);
    }
  };
  
  // FAQ data
  const faqData = [
    {
      id: 1,
      question: "O que é o E-Commerce App?",
      answer: "Somos uma empresa especializada em aumentar a conversão de e-commerces, unindo aplicativo mobile nativo e CRM inteligente, focado em análise de comportamento, segmentação avançada e campanhas personalizadas que geram mais vendas e retenção."
    },
    {
      id: 2,
      question: "Como Funciona?",
      answer: "Nosso aplicativo se conecta diretamente com sua plataforma de e-commerce, sincronizando produtos, estoque e pedidos automaticamente. Seus clientes podem navegar, comprar e acompanhar pedidos pelo app, enquanto você gerencia tudo pelo painel administrativo."
    },
    {
      id: 3,
      question: "Como funciona o checkout no aplicativo?",
      answer: "O checkout no aplicativo é seguro e otimizado para conversão. Oferecemos integração com as principais gateways de pagamento, permitindo pagamentos por cartão, PIX, boleto e outros métodos. Todo o processo é feito dentro do app, sem redirecionamentos."
    },
    {
      id: 4,
      question: "Eu vou ter o app na loja apple e google?",
      answer: "Sim! Seu aplicativo será publicado nas lojas oficiais da Apple (App Store) e Google (Play Store) com sua marca e identidade visual. Cuidamos de todo o processo de publicação e manutenção nas lojas."
    },
    {
      id: 5,
      question: "Quantas notificações Push posso enviar por mês?",
      answer: "Nossos planos incluem envios ilimitados de notificações push. Você pode enviar quantas notificações desejar para promover produtos, informar sobre novidades ou recuperar carrinhos abandonados."
    },
    {
      id: 6,
      question: "Todos meus clientes com app receberão notificação Push?",
      answer: "As notificações push são enviadas para todos os usuários que instalaram o app e permitiram o recebimento de notificações. Nossa taxa média de aceite é de 85%, muito superior à média do mercado. Também oferecemos ferramentas para incentivar os usuários a habilitarem as notificações."
    },
    {
      id: 7,
      question: "Como faço para personalizar o app?",
      answer: "A personalização é feita através do nosso painel administrativo. Você pode definir cores, logos, imagens de destaque e até mesmo personalizar a experiência de navegação. Nossa equipe de design também pode ajudar a criar uma identidade visual exclusiva para seu app."
    },
    {
      id: 8,
      question: "Preciso cadastrar meus produtos e categorias novamente?",
      answer: "Não! Nosso sistema sincroniza automaticamente com sua plataforma de e-commerce existente. Todos os produtos, categorias, preços e estoque são importados e atualizados em tempo real, sem necessidade de cadastro manual."
    },
    {
      id: 9,
      question: "Como começo?",
      answer: "É simples! Entre em contato conosco através do formulário no site ou pelo WhatsApp. Faremos uma demonstração personalizada e, se você gostar, podemos iniciar o desenvolvimento do seu app em até 48 horas. O processo completo até a publicação nas lojas leva em média 15 dias."
    }
  ];
  
  // Filter FAQs based on search term
  const filteredFaqs = searchTerm.trim() === "" 
    ? faqData 
    : faqData.filter(faq => 
        faq.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
        faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
      );

  return (
    <>
      <Head>
        <title>E-Commerce App | Solução Completa de E-commerce para Varejo e Farmácias</title>
        <meta name="description" content="Transforme seu negócio com nossa solução completa de e-commerce. Aplicativo nativo, integração com estoque, pagamentos seguros e muito mais para varejo e farmácias." />
        <meta name="keywords" content="e-commerce, aplicativo de vendas, varejo, farmácias, app nativo, checkout seguro, gestão de estoque" />
        <meta name="author" content="E-Commerce App" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="E-Commerce App | Solução Completa de E-commerce" />
        <meta property="og:description" content="Transforme seu negócio com nossa solução completa de e-commerce. Aplicativo nativo, integração com estoque, pagamentos seguros e muito mais." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://institucional-6pew3n12o-ecommerce-apps-projects-15da6505.vercel.app/" />
        <meta property="og:image" content="https://institucional-6pew3n12o-ecommerce-apps-projects-15da6505.vercel.app/images/logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="E-Commerce App | Solução Completa de E-commerce" />
        <meta name="twitter:description" content="Transforme seu negócio com nossa solução completa de e-commerce para varejo e farmácias." />
        <meta name="twitter:image" content="https://institucional-6pew3n12o-ecommerce-apps-projects-15da6505.vercel.app/images/logo.png" />
        <link rel="canonical" href="https://institucional-6pew3n12o-ecommerce-apps-projects-15da6505.vercel.app/" />
        <link rel="icon" href="/images/icone.png" />
        
        {/* Schema.org para Rich Snippets */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "SoftwareApplication",
              "name": "E-Commerce App",
              "applicationCategory": "BusinessApplication",
              "operatingSystem": "Android, iOS",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "BRL"
              },
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "4.8",
                "ratingCount": "1250"
              },
              "description": "Solução completa de e-commerce com aplicativo nativo para varejo e farmácias."
            })
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "E-Commerce App",
              "url": "https://institucional-6pew3n12o-ecommerce-apps-projects-15da6505.vercel.app/",
              "logo": "https://institucional-6pew3n12o-ecommerce-apps-projects-15da6505.vercel.app/images/logo.png",
              "sameAs": [
                "https://www.facebook.com/ecommerceapp",
                "https://www.instagram.com/ecommerceapp",
                "https://www.linkedin.com/company/ecommerceapp"
              ],
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+55-11-3456-7890",
                "contactType": "customer service",
                "availableLanguage": ["Portuguese", "English"]
              }
            })
          }}
        />
      </Head>
      <Header />
      <main className="relative min-h-screen overflow-hidden">
        {/* Hero Section */}
        <section className="relative">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 min-h-[calc(100vh-80px)]">
              
              {/* Left Side - Content */}
              <div className="flex flex-col justify-center py-12 lg:py-20">
                {/* Welcome Badge */}
                <div className="flex items-center gap-3 mb-8">
                  <span className="text-sm uppercase tracking-wider text-gray-500">Bem Vindo</span>
                  <span className="bg-emerald-900 text-white text-xs font-semibold uppercase tracking-wider px-4 py-1.5 rounded-full">
                    E-COMMERCEAPP
                  </span>
                </div>

                {/* Main Title - H1 principal para SEO */}
                <h1 className="text-5xl lg:text-6xl xl:text-7xl font-light leading-[1.1] text-zinc-900" id="inicio">
                  <span className="italic">Um app próprio</span>
                  <div className="flex items-center gap-4 my-2">
                    {/* Green Icon */}
                    <div className="flex items-center">
                      <div className="w-16 h-16 lg:w-20 lg:h-20 bg-gray-200 rounded-full flex items-center justify-center">
                        <div className="w-10 h-10 lg:w-12 lg:h-12 bg-emerald-700 rounded-lg flex items-center justify-center">
                          <div className="w-4 h-4 bg-lime-400 rounded-full"></div>
                        </div>
                      </div>
                    </div>
                    <span className="italic">para</span>
                  </div>
                  <span className="italic">escalar seu</span>
                  <br />
                  <span className="italic">e-commerce</span>
                  <sup className="text-base ml-1">®</sup>
                </h1>

                {/* Description */}
                <p className="mt-8 text-lg text-gray-600 max-w-md leading-relaxed">
                  Aumente vendas e retenção com app móvel nativo para seu ecommerce
                </p>

                {/* CTA Button */}
                <div className="mt-8">
                  <a 
                    href="#contato"
                    className="inline-block bg-lime-400 hover:bg-lime-500 text-emerald-900 font-semibold px-8 py-4 rounded-full text-lg transition-colors"
                  >
                    Solicitar avaliação gratuita
                  </a>
                </div>
              </div>

              {/* Right Side - Image Area */}
              <div className="relative hidden md:flex items-center justify-center lg:justify-end min-h-[500px]">
                {/* Green Background Shape */}
                <div className="absolute top-10 right-0 w-[90%] h-[85%] rounded-[3rem]" style={{backgroundColor: '#a3e635', zIndex: 1}}></div>
                
                {/* Phone Mockup */}
                <img 
                  src="https://io.convertiez.com.br/m/ecommerceapp/uploads/site_ecommerceapp/mockup_seguimentos.png" 
                  alt="App Mockup" 
                  className="absolute left-[-2%] w-96 lg:w-[370px] h-auto"
                  style={{zIndex: 5}}
                  width={500}
                  height={900}
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                />

                {/* Retention Badge */}
                <div className="absolute top-20 right-4 lg:right-8 bg-white rounded-2xl shadow-lg p-5 z-20 w-48 lg:w-56">
                  <div className="text-3xl lg:text-4xl font-bold text-emerald-600">+25%</div>
                  <div className="text-sm text-gray-600 mt-2 font-medium">aumento na<br/>retenção de clientes</div>
                  <div className="flex items-center gap-2 mt-3">
                    <span className="w-3 h-3 bg-emerald-600 rounded-full"></span>
                    <span className="text-sm text-gray-600 font-medium">Comprovado</span>
                  </div>
                </div>

                {/* Mobile Application Card */}
                <div className="absolute bottom-20 right-4 lg:right-8 bg-white rounded-2xl shadow-lg p-5 z-20">
                  <div className="flex items-center gap-2 text-xl font-semibold text-zinc-900">
                    Aplicativo
                    <span className="w-6 h-6 bg-lime-400 rounded-full flex items-center justify-center text-xs">⚡</span>
                  </div>
                  <div className="text-xl font-semibold text-zinc-900">Demonstrativo</div>
                  <a href="#download" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-zinc-700 hover:text-zinc-900">
                    Baixar app
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="ml-1">
                      <path d="M4 12l8-8M12 4v8M12 4H4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </div>
              </div>

            </div>
          </div>

        </section>

        {/* Clients Logos Bar - Marcas que confiam */}
        <div className="w-full">
          <div className="bg-emerald-900 py-5 px-6 overflow-hidden w-full">
            <div className="text-center mb-8">
              <h3 className="text-xl lg:text-2xl font-bold text-white">Clientes que confiam em nosso app</h3>
            </div>
            <div className="flex animate-scroll">
              <div className="flex items-center gap-12 shrink-0">
                <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/site/petcamp_logo_2.png" alt="Petcamp" className="h-12 w-auto object-contain" loading="lazy" decoding="async" width="120" height="48" />
                <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/site/drogalima_logo_2.png" alt="Drogalima" className="h-12 w-auto object-contain" loading="lazy" decoding="async" width="120" height="48" />
                <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/site/drogalider_logo_2.png" alt="Drogalider" className="h-12 w-auto object-contain" loading="lazy" decoding="async" width="120" height="48" />
                <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/site/brito_logo_2.png" alt="Farmacia Brito" className="h-12 w-auto object-contain" loading="lazy" decoding="async" width="120" height="48" />
                <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/site/amparo_logo_2.png" alt="Amparo Farmacias" className="h-12 w-auto object-contain" loading="lazy" decoding="async" width="120" height="48" />
                <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/site/saopaulo_logo_2.png" alt="Farmacias São Paulo" className="h-12 w-auto object-contain" loading="lazy" decoding="async" width="120" height="48" />
                <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/site/new_logo_2.png" alt="NewNutrition" className="h-12 w-auto object-contain" loading="lazy" decoding="async" width="120" height="48" />
                <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/site/silvarocha_logo_2.png" alt="Farmacia Silva Rocha" className="h-12 w-auto object-contain" loading="lazy" decoding="async" width="120" height="48" />
              </div>
              <div className="flex items-center gap-12 shrink-0 ml-12">
                <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/site/petcamp_logo_2.png" alt="Petcamp" className="h-12 w-auto object-contain" loading="lazy" decoding="async" width="120" height="48" />
                <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/site/drogalima_logo_2.png" alt="Drogalima" className="h-12 w-auto object-contain" loading="lazy" decoding="async" width="120" height="48" />
                <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/site/drogalider_logo_2.png" alt="Drogalider" className="h-12 w-auto object-contain" loading="lazy" decoding="async" width="120" height="48" />
                <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/site/brito_logo_2.png" alt="Farmacia Brito" className="h-12 w-auto object-contain" loading="lazy" decoding="async" width="120" height="48" />
                <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/site/amparo_logo_2.png" alt="Amparo Farmacias" className="h-12 w-auto object-contain" loading="lazy" decoding="async" width="120" height="48" />
                <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/site/saopaulo_logo_2.png" alt="Farmacias São Paulo" className="h-12 w-auto object-contain" loading="lazy" decoding="async" width="120" height="48" />
                <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/site/new_logo_2.png" alt="NewNutrition" className="h-12 w-auto object-contain" loading="lazy" decoding="async" width="120" height="48" />
                <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/site/silvarocha_logo_2.png" alt="Farmacia Silva Rocha" className="h-12 w-auto object-contain" loading="lazy" decoding="async" width="120" height="48" />
              </div>
            </div>
          </div>
        </div>

        {/* Features Section - Focado em Resultados */}
        <section className="py-20 bg-gray-50">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            {/* Header */}
            <div className="text-center mb-16">
              <span className="inline-block bg-lime-400 text-emerald-900 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
                Resultados Comprovados
              </span>
              <h2 className="text-4xl lg:text-5xl font-light text-zinc-900 leading-tight mb-6" id="funcionalidades">
                Funcionalidades que<br />
                <span className="font-semibold">impactam suas vendas</span>
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
                Cada recurso do app foi pensado para gerar resultado real para o seu negócio. Veja como nossos clientes aumentam receita e fidelização.
              </p>
            </div>

            {/* Cards Grid - 3 principais */}
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              {/* Card 1 - Push = Retenção */}
              <div className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-lg transition-shadow border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center">
                    <svg className="w-7 h-7 text-emerald-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z"/>
                    </svg>
                  </div>
                  <span className="bg-emerald-50 text-emerald-700 text-xs font-bold px-3 py-1.5 rounded-full">
                    +40% retenção
                  </span>
                </div>
                <h3 className="text-xl font-bold text-zinc-900 mb-3">Push Notifications</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-5">
                  Traga clientes de volta com campanhas segmentadas. Sem custo por envio, diferente de SMS.
                </p>
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-emerald-600 font-medium text-sm">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                    Receita recorrente garantida
                  </div>
                </div>
              </div>

              {/* Card 2 - Checkout = Menos abandono */}
              <div className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-lg transition-shadow border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center">
                    <svg className="w-7 h-7 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
                    </svg>
                  </div>
                  <span className="bg-blue-50 text-blue-700 text-xs font-bold px-3 py-1.5 rounded-full">
                    -35% abandono
                  </span>
                </div>
                <h3 className="text-xl font-bold text-zinc-900 mb-3">Checkout Otimizado</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-5">
                  Pagamento em 2 cliques com dados salvos. Pix, cartão e carteiras digitais integrados.
                </p>
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-blue-600 font-medium text-sm">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                    Mais vendas finalizadas
                  </div>
                </div>
              </div>

              {/* Card 3 - Segmentação = Ticket médio */}
              <div className="bg-emerald-900 rounded-3xl p-8 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-lime-400/20 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div className="relative">
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-14 h-14 bg-lime-400 rounded-2xl flex items-center justify-center">
                      <svg className="w-7 h-7 text-emerald-900" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                      </svg>
                    </div>
                    <span className="bg-lime-400 text-emerald-900 text-xs font-bold px-3 py-1.5 rounded-full">
                      +25% ticket médio
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">Segmentação Inteligente</h3>
                  <p className="text-emerald-200 text-sm leading-relaxed mb-5">
                    Ofertas personalizadas baseadas em comportamento. Clientes certos, produtos certos.
                  </p>
                  <div className="pt-4 border-t border-emerald-700">
                    <div className="flex items-center gap-2 text-lime-400 font-medium text-sm">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                      EXCLUSIVO CRM 
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Segunda linha de cards */}
            <div className="grid md:grid-cols-3 gap-6">
              {/* Card 4 - Assinatura */}
              <div className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-lg transition-shadow border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center">
                    <svg className="w-7 h-7 text-amber-600" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
                  </div>
                  <span className="bg-amber-50 text-amber-700 text-xs font-bold px-3 py-1.5 rounded-full">
                    Receita previsível
                  </span>
                </div>
                <h3 className="text-xl font-bold text-zinc-900 mb-3">Assinatura / Compra Recorrente</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-5">
                  Clientes assinam produtos e recebem automaticamente. Receita garantida todo mês.
                </p>
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-amber-600 font-medium text-sm">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                    Faturamento recorrente garantido
                  </div>
                </div>
              </div>

              {/* Card 5 - Lista de Presentes */}
              <div className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-lg transition-shadow border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center">
                    <svg className="w-7 h-7 text-purple-600" fill="currentColor" viewBox="0 0 24 24"><path d="M20 6h-2.18c.11-.31.18-.65.18-1 0-1.66-1.34-3-3-3-1.05 0-1.96.54-2.5 1.35l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z"/></svg>
                  </div>
                  <span className="bg-purple-50 text-purple-700 text-xs font-bold px-3 py-1.5 rounded-full">
                    +Novos clientes
                  </span>
                </div>
                <h3 className="text-xl font-bold text-zinc-900 mb-3">Lista de Presentes</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-5">
                  Chás de bebê, casamentos e aniversários. Seus clientes compartilham e você vende mais.
                </p>
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-purple-600 font-medium text-sm">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                    Viralização orgânica
                  </div>
                </div>
              </div>

              {/* Card 6 - Vendas Segmentadas */}
              <div className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-lg transition-shadow border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <div className="w-14 h-14 bg-rose-100 rounded-2xl flex items-center justify-center">
                    <svg className="w-7 h-7 text-rose-600" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l-5.5 9h11L12 2zm0 3.84L13.93 9h-3.87L12 5.84zM17.5 13c-2.49 0-4.5 2.01-4.5 4.5s2.01 4.5 4.5 4.5 4.5-2.01 4.5-4.5-2.01-4.5-4.5-4.5zm0 7c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5zM3 21.5h8v-8H3v8zm2-6h4v4H5v-4z"/></svg>
                  </div>
                  <span className="bg-rose-50 text-rose-700 text-xs font-bold px-3 py-1.5 rounded-full">
                    Conversão máxima
                  </span>
                </div>
                <h3 className="text-xl font-bold text-zinc-900 mb-3">Mais Conversão</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-5">
                  Seus clientes compram em poucos toques, enquanto você realiza de 10 a 15 vendas por minuto.
                </p>
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-rose-600 font-medium text-sm">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                    Mais vendas com menos esforço
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center mt-12">
              <a href="#contato" className="inline-flex items-center bg-emerald-900 text-white font-medium px-8 py-4 rounded-full hover:bg-emerald-800 transition-colors">
                Quero esses resultados no meu negócio
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
              </a>
            </div>
          </div>
        </section>

        {/* Clients Section */}
        <section id="cases" className="py-20 bg-white">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            {/* Header */}
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-light text-zinc-900 leading-tight mb-6" id="cases">
                Aqui você está em boa companhia!<br />
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
                Mais de 50 mil pessoas já estão conectadas e comprando com a nossa tecnologia, todos os dias, em um ecossistema ativo e em constante crescimento.
              </p>
            </div>

            {/* Clients Map */}
            <div className="relative bg-gray-50 rounded-3xl p-8 lg:p-16 min-h-[500px]">
              {/* Center Logo */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                <div className="w-48 h-28 overflow-hidden">
                  <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/icones_clientes/bg_ecommerceapp_1.png" alt="Ecommerce App" className="w-full h-full object-contain" />
                </div>
              </div>

              {/* Connection Lines (decorative) */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full" style={{border: '1px dashed #d1d5db'}}></div>
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 rounded-full" style={{border: '1px dashed #e5e7eb'}}></div>
              
              {/* Connecting Lines */}
              <div className="absolute left-1/2 top-8 -translate-x-1/2 h-[calc(50%-8px)] w-px" style={{borderLeft: '1px dashed #e5e7eb', opacity: 0.6}}></div>
              <div className="absolute left-1/2 bottom-8 -translate-x-1/2 h-[calc(50%-8px)] w-px" style={{borderLeft: '1px dashed #e5e7eb', opacity: 0.6}}></div>
              <div className="absolute top-1/2 left-8 -translate-y-1/2 w-[calc(50%-8px)] h-px" style={{borderTop: '1px dashed #e5e7eb', opacity: 0.6}}></div>
              <div className="absolute top-1/2 right-8 -translate-y-1/2 w-[calc(50%-8px)] h-px" style={{borderTop: '1px dashed #e5e7eb', opacity: 0.6}}></div>
              
              {/* Diagonal Lines */}
              <div className="absolute left-1/4 top-24 w-[35%] h-px origin-left rotate-[30deg]" style={{borderTop: '1px dashed #e5e7eb', opacity: 0.6}}></div>
              <div className="absolute right-1/4 top-24 w-[35%] h-px origin-right -rotate-[30deg]" style={{borderTop: '1px dashed #e5e7eb', opacity: 0.6}}></div>
              <div className="absolute left-1/4 bottom-24 w-[35%] h-px origin-left -rotate-[30deg]" style={{borderTop: '1px dashed #e5e7eb', opacity: 0.6}}></div>
              <div className="absolute right-1/4 bottom-24 w-[35%] h-px origin-right rotate-[30deg]" style={{borderTop: '1px dashed #e5e7eb', opacity: 0.6}}></div>
              
              {/* Client 1 - Petcamp */}
              <div className="absolute top-8 left-8">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center cursor-pointer hover:shadow-lg hover:scale-110 transition-all duration-300 group relative">
                  <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/icones_clientes/icon_petcamp.png" alt="Petcamp" className="w-full h-full object-cover p-0" loading="lazy" decoding="async" width="64" height="64" />
                  {/* Hover Card */}
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-2xl shadow-xl p-4 opacity-0 invisible pointer-events-none group-hover:opacity-100 group-hover:visible group-hover:pointer-events-auto transition-all duration-300 z-20">
                    <div className="w-full h-32 bg-cover bg-center rounded-xl mb-3" style={{backgroundImage: "url('https://io.convertiez.com.br/m/ecommerceapp/uploads/icones_clientes/bg_petcamp.png')"}}>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">App de petshop com delivery e agendamento de serviços.</p>
                    <div className="flex flex-col space-y-2 mb-2">
                      <a href="https://play.google.com/store/apps/details?id=br.com.petcampbr" target="_blank" rel="noopener noreferrer" className="block">
                        <div className="flex items-center bg-black text-white px-4 py-2 rounded-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="mr-3">
                            <path d="M3.609 1.814L13.792 12 3.609 22.186c-.181.181-.29.423-.29.684 0 .551.448.999.999.999.26 0 .503-.109.684-.29L15.6 12 5.002 1.401a.996.996 0 0 0-1.683.413.993.993 0 0 0 .29.684zM10.885 1.404L20.599 12 10.885 22.597a.996.996 0 0 0-.29.684.993.993 0 0 0 .999.999c.26 0 .503-.109.684-.29L22.996 12 12.278.71A.999.999 0 0 0 10.6.71a.995.995 0 0 0 .285.694z"/>
                          </svg>
                          <div>
                            <div className="text-xs">DISPONÍVEL NO</div>
                            <div className="text-xl font-semibold">Google Play</div>
                          </div>
                        </div>
                      </a>
                      <a href="https://apps.apple.com/br/app/petcamp/id1234567890" target="_blank" rel="noopener noreferrer" className="block">
                        <div className="flex items-center bg-black text-white px-4 py-2 rounded-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="mr-3">
                            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                          </svg>
                          <div>
                            <div className="text-xs">BAIXAR NA</div>
                            <div className="text-xl font-semibold">App Store</div>
                          </div>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Client 2 - New Nutrition */}
              <div className="absolute top-24 left-1/4">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center cursor-pointer hover:shadow-lg hover:scale-110 transition-all duration-300 group relative">
                  <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/icones_clientes/icon_new.png" alt="New Nutrition" className="w-full h-full object-cover p-0" loading="lazy" decoding="async" width="64" height="64" />
                  {/* Hover Card */}
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-2xl shadow-xl p-4 opacity-0 invisible pointer-events-none group-hover:opacity-100 group-hover:visible group-hover:pointer-events-auto transition-all duration-300 z-20">
                    <div className="w-full h-32 bg-cover bg-center rounded-xl mb-3" style={{backgroundImage: "url('https://io.convertiez.com.br/m/ecommerceapp/uploads/icones_clientes/bg_new.png')"}}>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">App de suplementos e produtos naturais com entrega rápida.</p>
                    <div className="flex flex-col space-y-2 mb-2">
                      <a href="https://play.google.com/store/apps/details?id=br.com.newnutrition" target="_blank" rel="noopener noreferrer" className="block">
                        <div className="flex items-center bg-black text-white px-4 py-2 rounded-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="mr-3">
                            <path d="M3.609 1.814L13.792 12 3.609 22.186c-.181.181-.29.423-.29.684 0 .551.448.999.999.999.26 0 .503-.109.684-.29L15.6 12 5.002 1.401a.996.996 0 0 0-1.683.413.993.993 0 0 0 .29.684zM10.885 1.404L20.599 12 10.885 22.597a.996.996 0 0 0-.29.684.993.993 0 0 0 .999.999c.26 0 .503-.109.684-.29L22.996 12 12.278.71A.999.999 0 0 0 10.6.71a.995.995 0 0 0 .285.694z"/>
                          </svg>
                          <div>
                            <div className="text-xs">DISPONÍVEL NO</div>
                            <div className="text-xl font-semibold">Google Play</div>
                          </div>
                        </div>
                      </a>
                      <a href="https://apps.apple.com/br/app/newnutrition/id1234567890" target="_blank" rel="noopener noreferrer" className="block">
                        <div className="flex items-center bg-black text-white px-4 py-2 rounded-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="mr-3">
                            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                          </svg>
                          <div>
                            <div className="text-xs">BAIXAR NA</div>
                            <div className="text-xl font-semibold">App Store</div>
                          </div>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Client 3 - Drogalider */}
              <div className="absolute top-8 right-8">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center cursor-pointer hover:shadow-lg hover:scale-110 transition-all duration-300 group relative">
                  <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/icones_clientes/icon_lider.png" alt="Drogalider" className="w-full h-full object-cover p-0" loading="lazy" decoding="async" width="64" height="64" />
                  {/* Hover Card */}
                  <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-2xl shadow-xl p-4 opacity-0 invisible pointer-events-none group-hover:opacity-100 group-hover:visible group-hover:pointer-events-auto transition-all duration-300 z-20">
                    <div className="w-full h-32 bg-cover bg-center rounded-xl mb-3" style={{backgroundImage: "url('https://io.convertiez.com.br/m/ecommerceapp/uploads/icones_clientes/bg_lider.png')"}}>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">App de farmácia com delivery 24h e consulta de medicamentos.</p>
                    <div className="flex flex-col space-y-2 mb-2">
                      <a href="https://play.google.com/store/apps/details?id=br.com.drogaliderbr" target="_blank" rel="noopener noreferrer" className="block">
                        <div className="flex items-center bg-black text-white px-4 py-2 rounded-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="mr-3">
                            <path d="M3.609 1.814L13.792 12 3.609 22.186c-.181.181-.29.423-.29.684 0 .551.448.999.999.999.26 0 .503-.109.684-.29L15.6 12 5.002 1.401a.996.996 0 0 0-1.683.413.993.993 0 0 0 .29.684zM10.885 1.404L20.599 12 10.885 22.597a.996.996 0 0 0-.29.684.993.993 0 0 0 .999.999c.26 0 .503-.109.684-.29L22.996 12 12.278.71A.999.999 0 0 0 10.6.71a.995.995 0 0 0 .285.694z"/>
                          </svg>
                          <div>
                            <div className="text-xs">DISPONÍVEL NO</div>
                            <div className="text-xl font-semibold">Google Play</div>
                          </div>
                        </div>
                      </a>
                      <a href="https://apps.apple.com/br/app/drogalider/id6670400303" target="_blank" rel="noopener noreferrer" className="block">
                        <div className="flex items-center bg-black text-white px-4 py-2 rounded-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="mr-3">
                            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                          </svg>
                          <div>
                            <div className="text-xs">BAIXAR NA</div>
                            <div className="text-xl font-semibold">App Store</div>
                          </div>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Client 4 - Drogalima */}
              <div className="absolute top-24 right-1/4">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center cursor-pointer hover:shadow-lg hover:scale-110 transition-all duration-300 group relative">
                  <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/icones_clientes/icon_lima.png" alt="Drogalima" className="w-full h-full object-cover p-0" loading="lazy" decoding="async" width="64" height="64" />
                  {/* Hover Card */}
                  <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-2xl shadow-xl p-4 opacity-0 invisible pointer-events-none group-hover:opacity-100 group-hover:visible group-hover:pointer-events-auto transition-all duration-300 z-20">
                    <div className="w-full h-32 bg-cover bg-center rounded-xl mb-3" style={{backgroundImage: "url('https://io.convertiez.com.br/m/ecommerceapp/uploads/icones_clientes/bg_lima.png')"}}>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">App de farmácia com entrega rápida e descontos exclusivos.</p>
                    <div className="flex flex-col space-y-2 mb-2">
                      <a href="https://play.google.com/store/apps/details?id=br.com.drogalima" target="_blank" rel="noopener noreferrer" className="block">
                        <div className="flex items-center bg-black text-white px-4 py-2 rounded-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="mr-3">
                            <path d="M3.609 1.814L13.792 12 3.609 22.186c-.181.181-.29.423-.29.684 0 .551.448.999.999.999.26 0 .503-.109.684-.29L15.6 12 5.002 1.401a.996.996 0 0 0-1.683.413.993.993 0 0 0 .29.684zM10.885 1.404L20.599 12 10.885 22.597a.996.996 0 0 0-.29.684.993.993 0 0 0 .999.999c.26 0 .503-.109.684-.29L22.996 12 12.278.71A.999.999 0 0 0 10.6.71a.995.995 0 0 0 .285.694z"/>
                          </svg>
                          <div>
                            <div className="text-xs">DISPONÍVEL NO</div>
                            <div className="text-xl font-semibold">Google Play</div>
                          </div>
                        </div>
                      </a>
                      <a href="https://apps.apple.com/br/app/drogalima/id1234567890" target="_blank" rel="noopener noreferrer" className="block">
                        <div className="flex items-center bg-black text-white px-4 py-2 rounded-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="mr-3">
                            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                          </svg>
                          <div>
                            <div className="text-xs">BAIXAR NA</div>
                            <div className="text-xl font-semibold">App Store</div>
                          </div>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Client 5 - Oboto */}
              <div className="absolute top-1/2 -translate-y-1/2 left-8">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center cursor-pointer hover:shadow-lg hover:scale-110 transition-all duration-300 group relative">
                  <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/icones_clientes/icon_oboto.png" alt="Oboto" className="w-full h-full object-cover p-0" loading="lazy" decoding="async" width="64" height="64" />
                  {/* Hover Card */}
                  <div className="absolute bottom-0 left-0 mb-2 w-64 bg-white rounded-2xl shadow-xl p-4 opacity-0 invisible pointer-events-none group-hover:opacity-100 group-hover:visible group-hover:pointer-events-auto transition-all duration-300 z-20">
                    <div className="w-full h-32 bg-cover bg-center rounded-xl mb-3" style={{backgroundImage: "url('https://io.convertiez.com.br/m/ecommerceapp/uploads/icones_clientes/bg_oboto.png')"}}>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">App de cosméticos e perfumaria com programa de fidelidade.</p>
                    <div className="flex flex-col space-y-2 mb-2">
                      <a href="https://play.google.com/store/apps/details?id=br.com.oboto" target="_blank" rel="noopener noreferrer" className="block">
                        <div className="flex items-center bg-black text-white px-4 py-2 rounded-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="mr-3">
                            <path d="M3.609 1.814L13.792 12 3.609 22.186c-.181.181-.29.423-.29.684 0 .551.448.999.999.999.26 0 .503-.109.684-.29L15.6 12 5.002 1.401a.996.996 0 0 0-1.683.413.993.993 0 0 0 .29.684zM10.885 1.404L20.599 12 10.885 22.597a.996.996 0 0 0-.29.684.993.993 0 0 0 .999.999c.26 0 .503-.109.684-.29L22.996 12 12.278.71A.999.999 0 0 0 10.6.71a.995.995 0 0 0 .285.694z"/>
                          </svg>
                          <div>
                            <div className="text-xs">DISPONÍVEL NO</div>
                            <div className="text-xl font-semibold">Google Play</div>
                          </div>
                        </div>
                      </a>
                      <a href="https://apps.apple.com/br/app/oboto/id1234567890" target="_blank" rel="noopener noreferrer" className="block">
                        <div className="flex items-center bg-black text-white px-4 py-2 rounded-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="mr-3">
                            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                          </svg>
                          <div>
                            <div className="text-xs">BAIXAR NA</div>
                            <div className="text-xl font-semibold">App Store</div>
                          </div>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Client 6 - Amparo */}
              <div className="absolute top-1/2 -translate-y-1/2 right-8">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center cursor-pointer hover:shadow-lg hover:scale-110 transition-all duration-300 group relative">
                  <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/icones_clientes/icon_amparo.png" alt="Amparo" className="w-full h-full object-cover p-0" loading="lazy" decoding="async" width="64" height="64" />
                  {/* Hover Card */}
                  <div className="absolute bottom-0 right-0 mb-2 w-64 bg-white rounded-2xl shadow-xl p-4 opacity-0 invisible pointer-events-none group-hover:opacity-100 group-hover:visible group-hover:pointer-events-auto transition-all duration-300 z-20">
                    <div className="w-full h-32 bg-cover bg-center rounded-xl mb-3" style={{backgroundImage: "url('https://io.convertiez.com.br/m/ecommerceapp/uploads/icones_clientes/bg_amparo.png')"}}>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">App de farmácia com delivery rápido e atendimento personalizado.</p>
                    <div className="flex flex-col space-y-2 mb-2">
                      <a href="https://play.google.com/store/apps/details?id=br.com.amparofarmacias" target="_blank" rel="noopener noreferrer" className="block">
                        <div className="flex items-center bg-black text-white px-4 py-2 rounded-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="mr-3">
                            <path d="M3.609 1.814L13.792 12 3.609 22.186c-.181.181-.29.423-.29.684 0 .551.448.999.999.999.26 0 .503-.109.684-.29L15.6 12 5.002 1.401a.996.996 0 0 0-1.683.413.993.993 0 0 0 .29.684zM10.885 1.404L20.599 12 10.885 22.597a.996.996 0 0 0-.29.684.993.993 0 0 0 .999.999c.26 0 .503-.109.684-.29L22.996 12 12.278.71A.999.999 0 0 0 10.6.71a.995.995 0 0 0 .285.694z"/>
                          </svg>
                          <div>
                            <div className="text-xs">DISPONÍVEL NO</div>
                            <div className="text-xl font-semibold">Google Play</div>
                          </div>
                        </div>
                      </a>
                      <a href="https://apps.apple.com/br/app/amparo-farmacias/id1234567890" target="_blank" rel="noopener noreferrer" className="block">
                        <div className="flex items-center bg-black text-white px-4 py-2 rounded-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="mr-3">
                            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                          </svg>
                          <div>
                            <div className="text-xs">BAIXAR NA</div>
                            <div className="text-xl font-semibold">App Store</div>
                          </div>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Client 7 - São Paulo */}
              <div className="absolute bottom-8 left-8">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center cursor-pointer hover:shadow-lg hover:scale-110 transition-all duration-300 group relative">
                  <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/icones_clientes/icon_saopaulo_1.png" alt="São Paulo" className="w-full h-full object-cover p-0" loading="lazy" decoding="async" width="64" height="64" />
                  {/* Hover Card */}
                  <div className="absolute bottom-full left-0 mb-2 w-64 bg-white rounded-2xl shadow-xl p-4 opacity-0 invisible pointer-events-none group-hover:opacity-100 group-hover:visible group-hover:pointer-events-auto transition-all duration-300 z-20">
                    <div className="w-full h-32 bg-cover bg-center rounded-xl mb-3" style={{backgroundImage: "url('https://io.convertiez.com.br/m/ecommerceapp/uploads/icones_clientes/bg_saopaulo_1.png')"}}>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">App de drogaria com entrega expressa e programa de pontos.</p>
                    <div className="flex flex-col space-y-2 mb-2">
                      <a href="https://play.google.com/store/apps/details?id=br.com.farmaciassaopaulo" target="_blank" rel="noopener noreferrer" className="block">
                        <div className="flex items-center bg-black text-white px-4 py-2 rounded-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="mr-3">
                            <path d="M3.609 1.814L13.792 12 3.609 22.186c-.181.181-.29.423-.29.684 0 .551.448.999.999.999.26 0 .503-.109.684-.29L15.6 12 5.002 1.401a.996.996 0 0 0-1.683.413.993.993 0 0 0 .29.684zM10.885 1.404L20.599 12 10.885 22.597a.996.996 0 0 0-.29.684.993.993 0 0 0 .999.999c.26 0 .503-.109.684-.29L22.996 12 12.278.71A.999.999 0 0 0 10.6.71a.995.995 0 0 0 .285.694z"/>
                          </svg>
                          <div>
                            <div className="text-xs">DISPONÍVEL NO</div>
                            <div className="text-xl font-semibold">Google Play</div>
                          </div>
                        </div>
                      </a>
                      <a href="https://apps.apple.com/br/app/farmacias-sao-paulo/id1234567890" target="_blank" rel="noopener noreferrer" className="block">
                        <div className="flex items-center bg-black text-white px-4 py-2 rounded-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="mr-3">
                            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                          </svg>
                          <div>
                            <div className="text-xs">BAIXAR NA</div>
                            <div className="text-xl font-semibold">App Store</div>
                          </div>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Client 8 - Silva Rocha */}
              <div className="absolute bottom-24 left-1/4">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center cursor-pointer hover:shadow-lg hover:scale-110 transition-all duration-300 group relative">
                  <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/icones_clientes/icon_silvarocha.png" alt="Silva Rocha" className="w-full h-full object-cover p-0" loading="lazy" decoding="async" width="64" height="64" />
                  {/* Hover Card */}
                  <div className="absolute bottom-full left-0 mb-2 w-64 bg-white rounded-2xl shadow-xl p-4 opacity-0 invisible pointer-events-none group-hover:opacity-100 group-hover:visible group-hover:pointer-events-auto transition-all duration-300 z-20">
                    <div className="w-full h-32 bg-cover bg-center rounded-xl mb-3" style={{backgroundImage: "url('https://io.convertiez.com.br/m/ecommerceapp/uploads/icones_clientes/bg_silvarocha_1.png')"}}>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">App de supermercado com delivery e ofertas exclusivas.</p>
                    <div className="flex flex-col space-y-2 mb-2">
                      <a href="https://play.google.com/store/apps/details?id=br.com.silvarocha" target="_blank" rel="noopener noreferrer" className="block">
                        <div className="flex items-center bg-black text-white px-4 py-2 rounded-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="mr-3">
                            <path d="M3.609 1.814L13.792 12 3.609 22.186c-.181.181-.29.423-.29.684 0 .551.448.999.999.999.26 0 .503-.109.684-.29L15.6 12 5.002 1.401a.996.996 0 0 0-1.683.413.993.993 0 0 0 .29.684zM10.885 1.404L20.599 12 10.885 22.597a.996.996 0 0 0-.29.684.993.993 0 0 0 .999.999c.26 0 .503-.109.684-.29L22.996 12 12.278.71A.999.999 0 0 0 10.6.71a.995.995 0 0 0 .285.694z"/>
                          </svg>
                          <div>
                            <div className="text-xs">DISPONÍVEL NO</div>
                            <div className="text-xl font-semibold">Google Play</div>
                          </div>
                        </div>
                      </a>
                      <a href="https://apps.apple.com/br/app/silva-rocha/id1234567890" target="_blank" rel="noopener noreferrer" className="block">
                        <div className="flex items-center bg-black text-white px-4 py-2 rounded-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="mr-3">
                            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                          </svg>
                          <div>
                            <div className="text-xs">BAIXAR NA</div>
                            <div className="text-xl font-semibold">App Store</div>
                          </div>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Client 9 - Brito */}
              <div className="absolute bottom-24 right-1/4">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center cursor-pointer hover:shadow-lg hover:scale-110 transition-all duration-300 group relative">
                  <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/icones_clientes/icon_brito.png" alt="Brito" className="w-full h-full object-cover p-0" loading="lazy" decoding="async" width="64" height="64" />
                  {/* Hover Card */}
                  <div className="absolute bottom-full right-0 mb-2 w-64 bg-white rounded-2xl shadow-xl p-4 opacity-0 invisible pointer-events-none group-hover:opacity-100 group-hover:visible group-hover:pointer-events-auto transition-all duration-300 z-20">
                    <div className="w-full h-32 bg-cover bg-center rounded-xl mb-3" style={{backgroundImage: "url('https://io.convertiez.com.br/m/ecommerceapp/uploads/icones_clientes/bg_brito.png')"}}>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">App de supermercado com delivery e ofertas personalizadas.</p>
                    <div className="flex flex-col space-y-2 mb-2">
                      <a href="https://play.google.com/store/apps/details?id=br.com.farmaciabrito" target="_blank" rel="noopener noreferrer" className="block">
                        <div className="flex items-center bg-black text-white px-4 py-2 rounded-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="mr-3">
                            <path d="M3.609 1.814L13.792 12 3.609 22.186c-.181.181-.29.423-.29.684 0 .551.448.999.999.999.26 0 .503-.109.684-.29L15.6 12 5.002 1.401a.996.996 0 0 0-1.683.413.993.993 0 0 0 .29.684zM10.885 1.404L20.599 12 10.885 22.597a.996.996 0 0 0-.29.684.993.993 0 0 0 .999.999c.26 0 .503-.109.684-.29L22.996 12 12.278.71A.999.999 0 0 0 10.6.71a.995.995 0 0 0 .285.694z"/>
                          </svg>
                          <div>
                            <div className="text-xs">DISPONÍVEL NO</div>
                            <div className="text-xl font-semibold">Google Play</div>
                          </div>
                        </div>
                      </a>
                      <a href="https://apps.apple.com/br/app/farmacia-brito/id1234567890" target="_blank" rel="noopener noreferrer" className="block">
                        <div className="flex items-center bg-black text-white px-4 py-2 rounded-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="mr-3">
                            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                          </svg>
                          <div>
                            <div className="text-xs">BAIXAR NA</div>
                            <div className="text-xl font-semibold">App Store</div>
                          </div>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Client 10 - Vera Cruz */}
              <div className="absolute bottom-8 right-8">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center cursor-pointer hover:shadow-lg hover:scale-110 transition-all duration-300 group relative">
                  <img src="https://io.convertiez.com.br/m/ecommerceapp/uploads/icones_clientes/icon_veracruz.png" alt="Vera Cruz" className="w-full h-full object-cover p-0" loading="lazy" decoding="async" width="64" height="64" />
                  {/* Hover Card */}
                  <div className="absolute bottom-full right-0 mb-2 w-64 bg-white rounded-2xl shadow-xl p-4 opacity-0 invisible pointer-events-none group-hover:opacity-100 group-hover:visible group-hover:pointer-events-auto transition-all duration-300 z-20">
                    <div className="w-full h-32 bg-cover bg-center rounded-xl mb-3" style={{backgroundImage: "url('https://io.convertiez.com.br/m/ecommerceapp/uploads/icones_clientes/bg_vera_cruz.png')"}}>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">App de farmácia com delivery 24h e atendimento especializado.</p>
                    <div className="flex flex-col space-y-2 mb-2">
                      <a href="https://play.google.com/store/apps/details?id=br.com.veracruz" target="_blank" rel="noopener noreferrer" className="block">
                        <div className="flex items-center bg-black text-white px-4 py-2 rounded-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="mr-3">
                            <path d="M3.609 1.814L13.792 12 3.609 22.186c-.181.181-.29.423-.29.684 0 .551.448.999.999.999.26 0 .503-.109.684-.29L15.6 12 5.002 1.401a.996.996 0 0 0-1.683.413.993.993 0 0 0 .29.684zM10.885 1.404L20.599 12 10.885 22.597a.996.996 0 0 0-.29.684.993.993 0 0 0 .999.999c.26 0 .503-.109.684-.29L22.996 12 12.278.71A.999.999 0 0 0 10.6.71a.995.995 0 0 0 .285.694z"/>
                          </svg>
                          <div>
                            <div className="text-xs">DISPONÍVEL NO</div>
                            <div className="text-xl font-semibold">Google Play</div>
                          </div>
                        </div>
                      </a>
                      <a href="https://apps.apple.com/br/app/vera-cruz/id1234567890" target="_blank" rel="noopener noreferrer" className="block">
                        <div className="flex items-center bg-black text-white px-4 py-2 rounded-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="mr-3">
                            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                          </svg>
                          <div>
                            <div className="text-xs">BAIXAR NA</div>
                            <div className="text-xl font-semibold">App Store</div>
                          </div>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
        </section>

        {/* FAQ Section */}
        <section id="faq" className="py-20 bg-gray-50">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            {/* Header */}
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-5xl font-light text-zinc-900 leading-tight mb-4">
                Perguntas Frequêntes
                <span className="inline-flex items-center ml-3">
                  <span className="w-12 h-12 bg-emerald-900 rounded-full flex items-center justify-center">
                    <span className="text-white text-2xl font-bold">?</span>
                  </span>
                </span>
              </h2>
              <p className="text-gray-600">
                Não encontrou o que procurava? É só perguntar!
              </p>
            </div>

            {/* Search */}
            <div className="max-w-xl mx-auto mb-12">
              <div className="relative">
                <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
                <input
                  type="text"
                  placeholder="Qual sua duvida?"
                  className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* FAQ Grid */}
            <div className="grid md:grid-cols-2 gap-4">
              {filteredFaqs.length > 0 ? (
                <>
                  {/* Left Column */}
                  <div className="space-y-4">
                    {filteredFaqs.slice(0, 4).map((faq) => (
                      <div key={faq.id} className="bg-white rounded-2xl p-6 shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-zinc-900">{faq.question}</h3>
                          <button 
                            onClick={() => toggleFaq(faq.id)} 
                            className="text-gray-400 text-2xl focus:outline-none"
                          >
                            {expandedFaqs[faq.id] ? '−' : '+'}
                          </button>
                        </div>
                        {expandedFaqs[faq.id] && (
                          <>
                            <p className="text-gray-600 text-sm leading-relaxed mb-4">
                              {faq.answer}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                              <span>Isso foi util?</span>
                              <button className="hover:text-emerald-600">👍</button>
                              <button className="hover:text-red-500">👎</button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    {filteredFaqs.slice(4).map((faq) => (
                      <div key={faq.id} className="bg-white rounded-2xl p-6 shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-zinc-900">{faq.question}</h3>
                          <button 
                            onClick={() => toggleFaq(faq.id)} 
                            className="text-gray-400 text-2xl focus:outline-none"
                          >
                            {expandedFaqs[faq.id] ? '−' : '+'}
                          </button>
                        </div>
                        {expandedFaqs[faq.id] && (
                          <>
                            <p className="text-gray-600 text-sm leading-relaxed mb-4">
                              {faq.answer}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                              <span>Isso foi util?</span>
                              <button className="hover:text-emerald-600">👍</button>
                              <button className="hover:text-red-500">👎</button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="col-span-2 text-center py-10">
                  <p className="text-gray-500">Nenhuma pergunta encontrada para "{searchTerm}". Tente outra busca.</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* CTA Section - Fale Conosco */}
        <section id="fale-conosco" className="py-16 bg-white">
          <div className="mx-auto max-w-5xl px-6 lg:px-8">
            <div id="contato" className="relative bg-emerald-900 rounded-3xl px-12 py-16 lg:py-20 overflow-hidden w-full">
              {/* Decorative shapes */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-lime-400 rounded-bl-[4rem]"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-lime-400 rounded-tr-[4rem]"></div>
              
              {/* Content */}
              <div className="relative z-10 text-center">
                <h2 className="text-3xl lg:text-4xl font-light text-white mb-4">
                  Simplifique seu E-Commerce
                </h2>
                <p className="text-emerald-200 mb-8 max-w-lg mx-auto">
                  Preencha o formulário abaixo para conhecer as ferramentas do E-Commerce App.
                </p>
                <form onSubmit={handleFormSubmit} className="max-w-3xl mx-auto text-left">
                  {formSubmitted && (
                    <div className="mb-4 p-3 bg-green-100 text-green-800 rounded-lg text-center">
                      Mensagem enviada com sucesso! Entraremos em contato em breve.
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-6 mb-6">
                    <div>
                      <input
                        type="text"
                        name="nome"
                        placeholder="Nome"
                        required
                        disabled={formSubmitting}
                        className="w-full px-6 py-4 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-lime-400 bg-white text-gray-800"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        name="empresa"
                        placeholder="Empresa"
                        required
                        disabled={formSubmitting}
                        className="w-full px-6 py-4 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-lime-400 bg-white text-gray-800"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-6 mb-6">
                    <div>
                      <input
                        type="email"
                        name="email"
                        placeholder="Email"
                        required
                        disabled={formSubmitting}
                        className="w-full px-6 py-4 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-lime-400 bg-white text-gray-800"
                      />
                    </div>
                    <div>
                      <input
                        type="tel"
                        name="telefone"
                        placeholder="Telefone"
                        required
                        disabled={formSubmitting}
                        className="w-full px-6 py-4 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-lime-400 bg-white text-gray-800"
                        id="telefone"
                        onInput={handlePhoneInput}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-6 mb-6">
                    <div className="col-span-2">
                      <select
                        name="interesse"
                        required
                        disabled={formSubmitting}
                        className="w-full px-6 py-4 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-lime-400 bg-white text-gray-800"
                        defaultValue=""
                      >
                        <option value="" disabled>Principal interesse</option>
                        <option value="Aplicativo de ecommerce">Aplicativo de ecommerce</option>
                        <option value="CRM">CRM</option>
                      </select>
                    </div>
                  </div>
                  <div className="mb-6">
                    <textarea
                      name="mensagem"
                      placeholder="Mensagem (opcional)"
                      rows="4"
                      disabled={formSubmitting}
                      className="w-full px-6 py-4 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-lime-400 bg-white text-gray-800"
                    ></textarea>
                  </div>
                  <div className="text-center">
                    <button 
                      type="submit" 
                      disabled={formSubmitting}
                      className={`${formSubmitting ? 'bg-gray-300 cursor-not-allowed' : 'bg-lime-400 hover:bg-lime-300'} text-emerald-900 font-semibold px-10 py-4 rounded-full text-base transition-colors inline-block`}
                    >
                      {formSubmitting ? 'Enviando...' : 'Enviar'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 bg-white border-t border-gray-100">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            {/* Navigation Links */}
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6 mb-8">
              {/* Main Links */}
              <div className="flex flex-wrap items-center justify-center gap-2">
                <a href="/" className="px-4 py-2 text-sm text-zinc-700 border border-gray-200 rounded-full hover:bg-gray-50 transition-colors">Início</a>
                <a href="#funcionalidades-do-app-nativo" onClick={(e) => {
                  e.preventDefault();
                  const section = document.querySelector('section h2');
                  if (section && section.textContent.includes('Funcionalidades do')) {
                    section.parentElement.parentElement.scrollIntoView({ behavior: 'smooth' });
                  }
                }} className="px-4 py-2 text-sm text-zinc-700 border border-gray-200 rounded-full hover:bg-gray-50 transition-colors">Funcionalidades</a>
                <a href="#cases" onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#cases').scrollIntoView({ behavior: 'smooth' });
                }} className="px-4 py-2 text-sm text-zinc-700 border border-gray-200 rounded-full hover:bg-gray-50 transition-colors">Cases</a>
                <a href="#fale-conosco" onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#fale-conosco').scrollIntoView({ behavior: 'smooth' });
                }} className="px-4 py-2 text-sm text-zinc-700 border border-gray-200 rounded-full hover:bg-gray-50 transition-colors">Fale Conosco</a>
              </div>
            </div>
            
            {/* Bottom Info */}
            <div className="flex flex-col lg:flex-row items-center justify-between gap-4 mb-8">
              <span className="text-xs text-gray-400 uppercase tracking-wider">POWER BY ECOMMERCEAPP</span>
              <a href="#privacy" className="text-xs text-gray-400 uppercase tracking-wider hover:text-gray-600">POLITICA DE PRIVACIDADE</a>
            </div>

            {/* Large Logo */}
            <div className="text-center">
              <span className="text-6xl lg:text-8xl xl:text-9xl font-bold text-emerald-900 tracking-tight">
                E-Commerce App
              </span>
            </div>
          </div>
        </footer>
      </main>

      {/* Modal de Agradecimento */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-white bg-opacity-70 animate-fadeIn">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-lg border border-gray-100 transform animate-scaleIn relative overflow-hidden">
            {/* Elementos decorativos */}
            <div className="absolute top-0 right-0 w-24 h-24 bg-lime-100 rounded-bl-[3rem] -z-10 opacity-50"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-emerald-100 rounded-tr-[3rem] -z-10 opacity-50"></div>
            <div className="text-center relative z-10">
              <div className="w-20 h-20 bg-green-100 rounded-full mx-auto flex items-center justify-center mb-6 animate-bounce">
                <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Obrigado!</h3>
              <p className="text-gray-600 mb-6">Recebemos sua mensagem com sucesso. Nossa equipe entrará em contato em breve.</p>
              <button 
                onClick={() => setShowModal(false)}
                className="bg-lime-400 hover:bg-lime-500 text-emerald-900 font-semibold px-8 py-3.5 rounded-full text-sm transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Botão flutuante do WhatsApp */}
      <div className="fixed bottom-6 right-6 z-40">
        <a 
          href="https://api.whatsapp.com/send?l=pt_br&phone=5501630757683"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-green-500 hover:bg-green-600 w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-105 animate-pulse"
          onClick={(e) => {
            if (showWhatsappPopup) {
              e.preventDefault();
              setShowWhatsappPopup(false);
            }
          }}
        >
          <svg className="w-8 h-8" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path fill="#ffffff" d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.345.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
            <path fill="#ffffff" d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 22c-5.523 0-10-4.477-10-10S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" opacity="0"/>
          </svg>
        </a>
      </div>
      
      {/* Popup do consultor */}
      {showWhatsappPopup && (
        <div className="fixed bottom-24 right-6 z-30 w-80 bg-white rounded-xl shadow-xl border border-gray-200 animate-slideInUp">
          <div className="absolute top-2 right-2">
            <button 
              onClick={() => setShowWhatsappPopup(false)}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          
          <div className="p-4">
            <div className="flex flex-col items-center mb-4">
              <div className="mb-3 w-32 h-32 rounded-full overflow-hidden flex-shrink-0 border-2 border-green-500 shadow-lg" style={{boxShadow: '0 0 15px rgba(0,0,0,0.1)'}}>
                <img 
                  src="/images/foto_jhon.jpeg" 
                  alt="Jhonatan Pereira"
                  width="128"
                  height="128"
                  loading="eager"
                  decoding="sync"
                  className="w-full h-full"
                  style={{
                    objectFit: 'cover',
                    objectPosition: 'center',
                    imageRendering: 'auto',
                    maxWidth: '100%'
                  }}
                />
              </div>
              <div className="text-center">
                <h4 className="font-medium text-gray-900 text-lg">Jhonatan Pereira</h4>
                <p className="text-sm text-gray-500">Representante Comercial</p>
              </div>
            </div>
            
            <div className="bg-gray-100 rounded-lg p-4 mb-4 relative">
              <div className="absolute top-5 left-1/2 transform -translate-x-1/2 -translate-y-10 w-0 h-0 border-l-8 border-r-8 border-b-8 border-gray-100 border-t-0"></div>
              <p className="text-sm text-gray-700 text-center">
                Olá, meu nome é Jhonatan Pereira, sou represente comercial da EcommerceApp, Gostaria de ajuda com
              </p>
            </div>
            
            <div className="flex flex-col gap-2">
              <a 
                href="https://api.whatsapp.com/send?l=pt_br&phone=5501630757683&text=Olá,%20gostaria%20de%20saber%20mais%20sobre%20o%20Aplicativo%20de%20Ecommerce."
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-500 hover:bg-green-600 text-white text-sm font-medium py-2 px-4 rounded-full transition-colors flex items-center justify-center"
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path fill="currentColor" d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.345.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"></path>
                </svg>
                Aplicativo de Ecommerce
              </a>
              <a 
                href="https://api.whatsapp.com/send?l=pt_br&phone=5501630757683&text=Olá,%20gostaria%20de%20saber%20mais%20sobre%20o%20CRM."
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-500 hover:bg-green-600 text-white text-sm font-medium py-2 px-4 rounded-full transition-colors flex items-center justify-center"
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path fill="currentColor" d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.345.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"></path>
                </svg>
                CRM
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // Adicionar estilos de animação
  const styles = `
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes scaleIn {
      from { transform: scale(0.95); opacity: 0; }
      to { transform: scale(1); opacity: 1; }
    }
    @keyframes slideInUp {
      from { transform: translateY(20px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    @keyframes pulse {
      0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(72, 187, 120, 0.7); }
      70% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(72, 187, 120, 0); }
      100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(72, 187, 120, 0); }
    }
    .animate-fadeIn {
      animation: fadeIn 0.3s ease-out;
    }
    .animate-scaleIn {
      animation: scaleIn 0.4s ease-out;
    }
    .animate-slideInUp {
      animation: slideInUp 0.5s ease-out;
    }
    .animate-pulse {
      animation: pulse 2s infinite;
    }
    .animate-bounce {
      animation: bounce 1s infinite;
    }
    @keyframes bounce {
      0%, 100% { transform: translateY(-5%); }
      50% { transform: translateY(0); }
    }
  `;

  // Adicionar estilos ao head e configurar timer para WhatsApp
  React.useEffect(() => {
    if (typeof document !== 'undefined') {
      const styleElement = document.createElement('style');
      styleElement.innerHTML = styles;
      document.head.appendChild(styleElement);
      
      // Aplicar máscara ao campo de telefone quando o componente montar
      const telefoneInput = document.getElementById('telefone');
      if (telefoneInput) {
        telefoneInput.addEventListener('input', handlePhoneInput);
      }
      
      
      return () => {
        document.head.removeChild(styleElement);
        if (telefoneInput) {
          telefoneInput.removeEventListener('input', handlePhoneInput);
        }
        // Timer já é limpo no useEffect específico
      };
    }
  }, []);
}
